import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Switch } from "@heroui/react";
import { ArrowUp, ArrowDown } from 'lucide-react';
import { PlayerStats } from "@/types";
import { useTranslation } from 'react-i18next';

export interface StatsInfo {
    enter_field_time: number | null;
    exit_field_time: number;
    replaced_by_player_id: number | null;
    replaced_player_id: number | null;
}

export interface Lineup {
    id: number;
    sport_id: number;
    fixture_id: number;
    player_id: number;
    team_id: number;
    position_id: number;
    formation_field: string | null;
    type_id: number;
    formation_position: number;
    player_name: string;
    jersey_number: number;
    formation: string;
    stats_info: StatsInfo | null;
}

interface FormationFieldProps {
    formation: string;
    rightStart?: boolean;
    players?: Lineup[];
    size: number;
    name: string;
    playerStats: PlayerStats[];
    selectedStat: string;
}

const getStatToDisplay = (playerStats: PlayerStats[], selectedStat: string, currentPlayerCounter: number, player?: Lineup,) => {
    if (!selectedStat || selectedStat === "" || selectedStat === "JERSEY_NUMBER") {
        return player ? player.jersey_number : currentPlayerCounter;
    }

    if (!player) {
        return "-"
    }

    const playerStat = playerStats.find(ps => ps.player_id === player.player_id);

    if (!playerStat) {
        return "-";
    }

    const statValue = playerStat.data[selectedStat] || null;

    if (statValue === undefined || statValue === null) {
        return "-";
    }

    if (typeof statValue === 'number') {
        if (Number.isInteger(statValue)) {
            return statValue;
        }
        return statValue.toFixed(1);
    }

    if (selectedStat === 'CAPTAIN') {
        return "C";
    }

    return statValue;
}

function fillFormationPositionForReplacedPlayers(players: Lineup[]) {
    // Originally, players that were replaced by other players did not have their formation_position filled.
    // This function fills the formation_position for those players based on the replaced_by_player_id.
    // The player who replaced them will have the same formation_position as the player they replaced.
    // Also sets formation_field.
    for (const player of players) {
        if (player.stats_info?.replaced_player_id) {
            const replacedPlayer = players.find(p => p.player_id === player.stats_info?.replaced_player_id);
            if (replacedPlayer) {
                player.formation_position = replacedPlayer.formation_position;
                player.formation_field = replacedPlayer.formation_field;
            }
        }
    }
    return players;
}

const FormationField: React.FC<FormationFieldProps> = ({ formation, rightStart, players, size, name, playerStats, selectedStat }) => {
    const { t } = useTranslation();
    const [showStarters, setShowStarters] = useState(true);
    const playersWithReplacedPositions = fillFormationPositionForReplacedPlayers(players || []);

    const sizeConst = size / 3;
    const FIELD_WIDTH = 3 * sizeConst;
    const FIELD_HEIGHT = 2 * sizeConst;
    const FONT_CONST = 22;


    let rows = formation.split('-').map((row: string) => parseInt(row));
    rows.unshift(1);

    const horizontalPaddingRatio = 0.1;
    const verticalPaddingRatio = -0.1;

    const horizontalEdgePadding = FIELD_WIDTH * horizontalPaddingRatio;
    const verticalEdgePadding = FIELD_HEIGHT * verticalPaddingRatio;

    const drawableWidth = FIELD_WIDTH * (1 - 2 * horizontalPaddingRatio);
    const drawableHeight = FIELD_HEIGHT * (1 - 2 * verticalPaddingRatio);

    const hasSubstitutionInformation = playersWithReplacedPositions.some(player => player.stats_info?.replaced_by_player_id || player.stats_info?.replaced_player_id);

    const newDeltaRows = rows.length > 1 ? drawableWidth / (rows.length - 1) : 0;

    const newRowsSpacingMap = rows.map((rowCount: number) => {
        return drawableHeight / (rowCount + 1);
    });

    interface PlayerProps {
        x: number;
        y: number;
        number: number | string;
        fullName: string;
        statsInfo: StatsInfo | null;
    }
    const Player: React.FC<PlayerProps> = ({ x, y, number, fullName, statsInfo }) => {
        let circleFillClass = "fill-primary";
        let baseName = fullName ? fullName.split(' ')[0] : "";

        const displayText = baseName;

        const si = statsInfo;
        const wasSubstitutedOut = !!si?.replaced_by_player_id;
        const wasSubstitutedIn = !!si?.replaced_player_id;

        if (wasSubstitutedOut) {
            circleFillClass = "fill-rose-500";
        } else if (wasSubstitutedIn) {
            circleFillClass = "fill-blue-500";
        }

        const iconSize = FIELD_HEIGHT / 23;
        const circleRadius = FIELD_HEIGHT / 20;

        const iconPlacedX = -iconSize / 2;
        let iconPlacedY = -circleRadius - iconSize - (FIELD_HEIGHT / 150);

        if (rightStart) {
            iconPlacedY = circleRadius + iconSize + (FIELD_HEIGHT / 150) - 11;
        }

        return (
            <g transform={`translate(${x},${y})`}  >
                {wasSubstitutedOut && (
                    <ArrowDown
                        x={iconPlacedX}
                        y={iconPlacedY}
                        size={iconSize}
                        className="text-rose-500"
                        strokeWidth={2.5}
                    />
                )}
                {wasSubstitutedIn && (
                    <ArrowUp
                        x={iconPlacedX}
                        y={iconPlacedY}
                        size={iconSize}
                        className="text-blue-500"
                        strokeWidth={2.5}
                    />
                )}
                <circle
                    r={circleRadius}
                    className={`transition-all duration-300 ${circleFillClass}`}
                />
                <text
                    fill="black"
                    textAnchor="middle"
                    dy={FIELD_HEIGHT / 40}
                    style={{ fontSize: FIELD_HEIGHT / (1.6 * FONT_CONST / 2) + "px" }}
                    className={" font-bold " + (rightStart ? " rotate-180" : "")}
                >
                    {number}
                </text>
                <text
                    y={FIELD_HEIGHT / 20 * 2}
                    textAnchor="middle"
                    style={{ fontSize: FIELD_HEIGHT / FONT_CONST + "px" }}
                    className={" fill-default-900  font-normal " + (rightStart ? " rotate-180" : "")}
                >
                    {displayText}
                </text>
            </g>
        );
    };

    return (
        <div className="flex flex-col gap-1 min-w-sm w-full">
            <div className="text-center text-sm text-default-500">
                {name}: {formation}
            </div>

            <div className={"max-w-6xl inline-block bg-transparent p-0 rounded-lg"
                + (rightStart ? " rotate-180" : "")

            } style={{ width: '100%' }}>
                <svg
                    viewBox={`0 0 ${FIELD_WIDTH} ${FIELD_HEIGHT}`}
                    className="w-full"
                >
                    <rect
                        width={FIELD_WIDTH}
                        height={FIELD_HEIGHT}
                        fill="transparent"
                        className="stroke-default-700"
                    />

                    <g className="stroke-default-300" strokeWidth="1" fill="none">
                        <rect
                            x="1"
                            y="1"
                            width={FIELD_WIDTH - 2}
                            height={FIELD_HEIGHT - 2}
                        />

                        <rect
                            x="1"
                            y={FIELD_HEIGHT * 0.15}
                            width={FIELD_WIDTH * 0.3}
                            height={FIELD_HEIGHT * 0.7}
                        />

                        <rect
                            x="1"
                            y={FIELD_HEIGHT * 0.35}
                            width={FIELD_WIDTH * 0.1}
                            height={FIELD_HEIGHT * 0.3}
                        />

                        <path
                            d={`M ${FIELD_WIDTH} ${FIELD_HEIGHT / 2 - FIELD_HEIGHT * 0.2} 
                        A ${FIELD_HEIGHT * 0.2} ${FIELD_HEIGHT * 0.2} 0 0 0 
                        ${FIELD_WIDTH} ${FIELD_HEIGHT / 2 + FIELD_HEIGHT * 0.2}`}
                        />
                    </g>

                    {rows.map((row: number, i: number) => {
                        let _players = [];
                        let playersReplaced = [];

                        for (let j = 0; j < row; j++) {
                            const currentPlayerCounter = j + rows.slice(0, i).reduce((acc, val) => acc + val, 0) + 1;
                            for (const player of playersWithReplacedPositions?.filter(player => player.formation_position === currentPlayerCounter) || []) {
                                if (player?.stats_info?.replaced_by_player_id) {
                                    playersReplaced.push(player.player_id);
                                }
                            }
                        }

                        for (let j = 0; j < row; j++) {
                            const currentPlayerCounter = j + rows.slice(0, i).reduce((acc, val) => acc + val, 0) + 1;
                            for (const player of playersWithReplacedPositions?.filter(player => player.formation_position === currentPlayerCounter) || []) {

                                const playerStartedFixture = (
                                    !(player?.stats_info) ||
                                    (player.stats_info.enter_field_time === null && player.stats_info.exit_field_time)
                                    ||
                                    (player.stats_info.enter_field_time === null && player.stats_info.exit_field_time === null)
                                );

                                let push = false;
                                if (!(player?.stats_info)) {
                                    // No info available for this fixture
                                    if (playerStartedFixture) push = true;
                                }
                                else if (showStarters) {
                                    // Show starters
                                    if (playerStartedFixture) {
                                        push = true;
                                    }
                                } else if (!showStarters) {
                                    // Show players who finished the game
                                    if (!playerStartedFixture || !playersReplaced.includes(player.player_id)) {
                                        push = true;
                                    }
                                }

                                if (push)
                                    _players.push(
                                        <Link to={player ? `/player/${player.player_id}` : '#'} key={player?.player_id}>
                                            <Player
                                                x={horizontalEdgePadding + newDeltaRows * i}
                                                y={verticalEdgePadding + newRowsSpacingMap[i] * (j + 1)}
                                                number={getStatToDisplay(playerStats, selectedStat, currentPlayerCounter, player)}
                                                fullName={player ? player.player_name.split(' ')[0] : ""}
                                                statsInfo={player ? player.stats_info : null}
                                            />
                                        </Link>
                                    );
                            }
                        }
                        return _players;
                    }
                    )}
                </svg>
            </div>
            {
                hasSubstitutionInformation &&
                <div className="text-center my-2 flex items-center space-x-2">
                    <Switch
                        isSelected={showStarters}
                        onValueChange={setShowStarters}
                        color="primary"
                        size='sm'
                        title={t('formation_field.formation_field_2')}
                    >
                        {t('formation_field.formation_field_3')} {showStarters ? t('formation_field.formation_field_4') : t('formation_field.formation_field_5')}
                    </Switch>
                </div>}
        </div>
    );
};

export default FormationField;